import type { CSSProperties, MouseEvent, ReactNode } from "react";
import MuiButton, { type ButtonProps as MuiButtonProps } from "@mui/material/Button";
import IconButton, { type IconButtonProps } from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";

// Thin wrapper mapping antd <Button> API onto MUI Button, so the ~28 call sites
// (type/loading/danger/shape/size/icon/block/htmlType) swap by import only and
// keep feature parity. Same justification as Tag/Tooltip wrappers.

type AntdType = "primary" | "default" | "dashed" | "text" | "link" | "ghost";
type AntdSize = "small" | "middle" | "large";
type AntdShape = "default" | "circle" | "round";

export interface ButtonProps {
  children?: ReactNode;
  type?: AntdType;
  size?: AntdSize;
  shape?: AntdShape;
  danger?: boolean;
  loading?: boolean;
  block?: boolean;
  /** antd standalone ghost (transparent bg) — mapped to a text/outlined look. */
  ghost?: boolean;
  icon?: ReactNode;
  disabled?: boolean;
  href?: string;
  htmlType?: "button" | "submit" | "reset";
  /** antd v5 color token — accepted for compat; MUI color is derived from danger. */
  color?: string;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  style?: CSSProperties;
  className?: string;
  id?: string;
  title?: string;
  "data-cy"?: string;
  "data-id"?: string;
  "data-testid"?: string;
  "aria-label"?: string;
}

// Icon-only buttons that sit in a toolbar/filter bar next to MUI size="small"
// inputs/selects (which render ~40px tall). Left to MUI, an icon button renders a
// different height and its glyph sits off-centre, so the row looks ragged. Apply
// this as `style` on the Button wrapper (with the glyph passed as CHILDREN, not the
// `icon` prop — see the circle/startIcon note below) or as `sx` on a MUI IconButton:
// it pins the button square at the input height and dead-centres the glyph.
// One shared value so every "icon button beside an input" lines up (issue #4897).
// 37px, not 40: this app's root font is 14px (legacyStyles custom_reset), so MUI
// size="small" inputs render at ~37px; a 40px icon button sits ~3px taller than
// its input neighbors. Match the real rendered input height here.
export const ICON_BUTTON_MATCH_INPUT = {
  height: "37px",
  width: "37px",
  minWidth: "37px",
  padding: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
} as const;

const VARIANT: Record<AntdType, MuiButtonProps["variant"]> = {
  primary: "contained",
  default: "outlined",
  dashed: "outlined",
  ghost: "outlined",
  text: "text",
  link: "text",
};

const SIZE: Record<AntdSize, MuiButtonProps["size"]> = {
  small: "small",
  middle: "medium",
  large: "large",
};

export const Button = ({
  children,
  type = "default",
  size = "middle",
  shape = "default",
  danger,
  loading,
  block,
  icon,
  disabled,
  href,
  htmlType,
  onClick,
  style,
  className,
  // pulled out so antd-only values don't leak to the MUI element:
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ghost,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  color: _antdColor,
  ...rest
}: ButtonProps) => {
  const color: MuiButtonProps["color"] = danger ? "error" : "primary";
  const spinner = <CircularProgress size={16} color="inherit" />;

  // antd circle button (icon-only) -> MUI IconButton. antd's default circle is a
  // compact (~32px) button with a thin border and a neutral (not blue) icon; MUI's
  // IconButton is larger, borderless, and colored. Restore antd's look: fixed compact
  // dims, a border for bordered types (default/dashed/ghost), neutral icon color
  // unless primary/danger, and a smaller glyph.
  if (shape === "circle") {
    const bordered = type === "default" || type === "dashed" || type === "ghost";
    const dim = size === "small" ? 28 : size === "large" ? 40 : 32;
    const iconColor: IconButtonProps["color"] = danger ? "error" : type === "primary" ? "primary" : "default";
    return (
      <IconButton
        color={iconColor}
        disabled={disabled || loading}
        href={href as string}
        onClick={onClick}
        style={style}
        className={className}
        sx={{
          width: dim,
          height: dim,
          ...(bordered ? { border: "1px solid", borderColor: "divider" } : {}),
          ...(type === "primary"
            ? { bgcolor: "primary.main", color: "primary.contrastText", "&:hover": { bgcolor: "primary.dark" } }
            : {}),
          "& svg": { fontSize: size === "large" ? 20 : 16 },
        }}
        {...rest}
      >
        {loading ? spinner : icon ?? children}
      </IconButton>
    );
  }

  return (
    <MuiButton
      variant={VARIANT[type]}
      color={color}
      size={SIZE[size]}
      disabled={disabled || loading}
      fullWidth={block}
      href={href}
      type={htmlType}
      onClick={onClick}
      startIcon={loading ? spinner : icon}
      // antd buttons keep their label on one line; MUI lets it wrap when squeezed
      // (e.g. the asset-allocation toolbar wrapped "Add Investment" to two lines).
      // minHeight 40 for the default (middle) size matches the 40px Input/Select height
      // so buttons don't sit ~3.5px short in a toolbar row (small/large keep MUI's height).
      sx={{ whiteSpace: "nowrap", ...(size === "middle" ? { minHeight: 40 } : {}) }}
      style={{ ...(type === "dashed" ? { borderStyle: "dashed" } : {}), ...(shape === "round" ? { borderRadius: 999 } : {}), ...style }}
      className={className}
      {...rest}
    >
      {children}
    </MuiButton>
  );
};

export default Button;
